"use client";

import { useEffect, useState } from "react";

interface UseRevealOptions {
	/** IntersectionObserver threshold. Default 0.15. */
	threshold?: number;
	/** IntersectionObserver rootMargin. */
	rootMargin?: string;
	/** Re-trigger when the element leaves and re-enters. Default false. */
	repeat?: boolean;
}

/**
 * Tracks whether an element is on-screen via IntersectionObserver and returns
 * a boolean to drive `data-visible`-based reveal animations.
 *
 * SSR-safe: starts `false` on the server and first client render; the observer
 * flips it after mount. Honors `prefers-reduced-motion` by skipping the
 * observer and reporting visible immediately.
 */
export const useReveal = <T extends Element = HTMLElement>({
	threshold = 0.15,
	rootMargin,
	repeat = false,
}: UseRevealOptions = {}) => {
	// Callback ref via useState so the effect re-runs as soon as React commits
	// the DOM node. A plain useRef is unreliable here because Reveal forwards
	// this ref through cloneElement + forwardRef; in React 19 production builds
	// the RefObject can still be null when the mount effect first runs.
	const [el, setEl] = useState<T | null>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (!el) return;

		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			setVisible(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry) return;
				if (entry.isIntersecting) {
					setVisible(true);
					if (!repeat) observer.disconnect();
				} else if (repeat) {
					setVisible(false);
				}
			},
			{ threshold, rootMargin },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [el, threshold, rootMargin, repeat]);

	return { ref: setEl, visible };
};
